import type { ChatFeed, ChatIssuance, ChatMessage, ChatState } from '../../services/conciergeChatTypes';

/**
 * The rows of the concierge chat, in order. A system message says only where something stands in
 * the feed (its `chc:` tag); what a card shows and in what state comes from the deposit's issuances,
 * never from message text. Rows that are not in Verbox at all — the transient state and the
 * "receipt from the phone" QR — are placed here.
 */
export type FeedRow =
  | { type: 'message'; message: ChatMessage; key: string }
  | { type: 'card'; issuance: ChatIssuance; receiptSent: boolean; key: string }
  | { type: 'event'; kind: 'receipt' | 'completed' | 'expired'; text: string; key: string }
  | { type: 'qr'; key: string }
  | { type: 'state'; state: ChatState; key: string };

const EVENT_KINDS = ['receipt', 'completed', 'expired'] as const;
type EventKind = (typeof EVENT_KINDS)[number];

const parseTag = (tags: string[]): { kind: string; id: number | null } | null => {
  const tag = tags.find((t) => t.startsWith('chc:'));
  if (!tag) return null;
  const [kind, id] = tag.slice(4).split(':');
  return { kind, id: id !== undefined && /^\d+$/.test(id) ? Number(id) : null };
};

export const buildRows = (feed: ChatFeed, { desktop }: { desktop: boolean }): FeedRow[] => {
  const byId = new Map(feed.issuances.map((i) => [i.id, i]));
  const rows: FeedRow[] = [];
  const placed = new Set<number>();

  feed.messages.forEach((message) => {
    const tag = parseTag(message.tags);
    if (tag?.kind === 'requisite') {
      const issuance = tag.id !== null ? byId.get(tag.id) : undefined;
      if (!issuance || placed.has(issuance.id)) return;
      placed.add(issuance.id);
      rows.push({ type: 'card', issuance, receiptSent: issuance.receiptSent, key: `c-${issuance.id}` });
      return;
    }
    if (tag && (EVENT_KINDS as readonly string[]).includes(tag.kind)) {
      rows.push({ type: 'event', kind: tag.kind as EventKind, text: message.text, key: `m-${message.id}` });
      return;
    }
    rows.push({ type: 'message', message, key: `m-${message.id}` });
  });

  // The message that places a card may not have reached Verbox yet: the active card is never hidden.
  const active = feed.issuances.find((i) => i.state === 'active');
  if (active && !placed.has(active.id)) {
    rows.push({ type: 'card', issuance: active, receiptSent: active.receiptSent, key: `c-${active.id}` });
  }

  // A receipt after a card means the client has paid there: the card drops its timer and buttons.
  let lastCard = -1;
  rows.forEach((row, index) => {
    if (row.type === 'card') lastCard = index;
    if (row.type === 'event' && row.kind === 'receipt' && lastCard >= 0) {
      const card = rows[lastCard] as Extract<FeedRow, { type: 'card' }>;
      rows[lastCard] = { ...card, receiptSent: true };
    }
  });

  if (desktop) {
    const activeIndex = rows.findIndex((r) => r.type === 'card' && r.issuance.state === 'active');
    const activeRow = activeIndex >= 0 ? (rows[activeIndex] as Extract<FeedRow, { type: 'card' }>) : null;
    if (activeRow && !activeRow.receiptSent) {
      rows.splice(activeIndex + 1, 0, { type: 'qr', key: 'qr' });
    } else if (feed.ticket.view === 'expired_with_hint') {
      rows.push({ type: 'qr', key: 'qr' });
    }
  }

  if (feed.state) rows.push({ type: 'state', state: feed.state, key: 'state' });

  return rows;
};
